import cn from 'classnames';
import { ComponentRef, forwardRef, useId } from 'react';
import { CheckIcon } from '@phosphor-icons/react/ssr';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { FieldRequiredMark, Text, CheckboxProps } from '@/shared/ui/core';
import styles from './checkbox.module.scss';


export const Checkbox = forwardRef<ComponentRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(function Checkbox ({
    className,
    wrapperClassName,
    label,
    description,
    error,
    state = 'default',
    disabled,
    required,
    ...props
}, ref) {
    const _id           = useId();
    const id            = props.id ?? _id;
    const describedBy   = error ? undefined : description ? `${ id }-description` : undefined;
    const errorId       = error ? `${ id }-error` : undefined;
    const isError       = state === 'error' || Boolean(error);
    const isSuccess     = state === 'success';
    const isWarning     = state === 'warning';
    const isDefault     = state === 'default';
    const textClassname = cn({
        [styles.default]    : isDefault,
        [styles.destructive]: isError,
        [styles.success]    : isSuccess,
        [styles.warning]    : isWarning,
        [styles.disabled]   : disabled,
    });

    return (
        <div className={ cn(styles.root, wrapperClassName) }>
            <CheckboxPrimitive.Root
                ref={ ref }
                disabled={ disabled }
                aria-invalid={ !!error }
                aria-describedby={ errorId ?? describedBy }
                className={ cn(styles.box, textClassname, className ) }
                { ...props }
                id={ id }
            >
                <CheckboxPrimitive.Indicator className={ styles.indicator }>
                    <CheckIcon size={ 12 }/>
                </CheckboxPrimitive.Indicator>
            </CheckboxPrimitive.Root>

            <div className={ styles.label_wrapper }>
                <Text
                    as="label"
                    size="sm"
                    tone="primary"
                    htmlFor={ id }
                    className={ cn(styles.label, textClassname) }
                >
                    { label }
                    {
                        required &&
                        <FieldRequiredMark/>
                    }
                </Text>

                {
                    isError &&
                    error &&
                    <Text
                        size="sm"
                        tone="destructive"
                        id={ errorId }
                        className={ styles.error }
                    >
                        { error }
                    </Text>
                }

                {
                    !isError &&
                    description &&
                    <Text
                        size="sm"
                        tone="secondary"
                        id={ describedBy }
                        className={ styles.description }
                    >
                        { description }
                    </Text>
                }
            </div>
        </div>
    );
});
